import { Anchor, Drawer, Stack, Table, Text, Title } from "@mantine/core";
import { useLocation } from "wouter";
import {
  ORIGIN_HEADINGS,
  forChannel,
  originTitle,
  rollupCities,
  rollupCountries,
  rollupLabels,
  sourceLabel,
} from "./trafficDisplay";

/**
 * What a channel is made of: hosts, campaigns, terms, countries, landing pages.
 *
 * Loaded from the same first-touch aggregates as the pie, filtered to the channel that was
 * clicked. Search keywords are often absent — engines stopped putting the query in the referrer,
 * and the beacon drops utm_term when the URL would otherwise overflow — so that section explains
 * itself rather than showing an empty table.
 */
export default function SourceDetailDrawer({
  opened,
  onClose,
  channel,
  sessions,
  origins,
  campaigns,
  media,
  terms,
  countries,
  cities,
  landings,
}) {
  const [, setLocation] = useLocation();

  const originRows = rollupLabels(forChannel(origins, channel), "origin");
  const campaignRows = rollupLabels(forChannel(campaigns, channel), "campaign");
  const mediumRows = rollupLabels(forChannel(media, channel), "medium");
  const termRows = rollupLabels(forChannel(terms, channel), "term");
  const countryRows = rollupCountries(forChannel(countries, channel));
  const landingRows = rollupLabels(forChannel(landings, channel), "url");
  const showKeywordNote = channel === "search" || channel === "organic";

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Stack gap={2}>
          <Title order={4}>{sourceLabel(channel) ?? "Channel"} traffic</Title>
          <Text size="sm" c="dimmed">
            {(sessions ?? 0).toLocaleString()} session{sessions === 1 ? "" : "s"}, first arrival
          </Text>
        </Stack>
      }
    >
      <Stack gap="lg">
        <BreakdownTable
          title={ORIGIN_HEADINGS[channel] ?? "Origins"}
          rows={originRows}
          empty={
            channel === "direct"
              ? null
              : "No referring host or campaign source was recorded for these sessions."
          }
          renderLabel={(row) => (
            <Stack gap={0}>
              <Text size="sm">{originTitle(row.label)}</Text>
              {originTitle(row.label) !== row.label && (
                <Text size="xs" c="dimmed">
                  {row.label}
                </Text>
              )}
            </Stack>
          )}
        />

        <BreakdownTable
          title="Campaigns"
          rows={campaignRows}
          empty={channel === "paid" ? "No utm_campaign on these paid sessions." : null}
        />

        <BreakdownTable title="Medium" rows={mediumRows} />

        {termRows.length > 0 ? (
          <BreakdownTable title="Keywords" rows={termRows} />
        ) : showKeywordNote ? (
          <Stack gap={4}>
            <Title order={5}>Keywords</Title>
            <Text size="sm" c="dimmed">
              Search engines no longer send the query that brought someone here. Google search
              queries are on{" "}
              <Anchor size="sm" onClick={() => setLocation("/search-console")}>
                Search Console
              </Anchor>
              .
            </Text>
          </Stack>
        ) : null}

        <BreakdownTable
          title="Countries"
          rows={countryRows.map((c) => ({ label: c.name || c.code, sessions: c.sessions, code: c.code }))}
          empty="No country was recorded for these sessions."
        />

        {countryRows.length > 0 && (
          <CityPreview cities={rollupCities(forChannel(cities, channel))} />
        )}

        <BreakdownTable
          title="Landing pages"
          rows={landingRows}
          empty="No landing page was recorded."
          renderLabel={(row) => (
            <Text size="sm" lineClamp={1} title={row.label}>
              {displayPath(row.label)}
            </Text>
          )}
        />
      </Stack>
    </Drawer>
  );
}

function CityPreview({ cities }) {
  if (!cities.length) return null;
  const top = cities.slice(0, 8);
  return (
    <Stack gap={4}>
      <Title order={5}>Cities</Title>
      <Text size="sm">
        {top.map((c, i) => (
          <span key={`${c.countryCode}-${c.city}`}>
            {i > 0 ? " · " : ""}
            {c.city}
            <Text span size="xs" c="dimmed">
              {" "}
              ({c.sessions.toLocaleString()})
            </Text>
          </span>
        ))}
        {cities.length > top.length ? " · …" : ""}
      </Text>
    </Stack>
  );
}

function BreakdownTable({ title, rows, empty, renderLabel }) {
  if (!rows?.length) {
    if (!empty) return null;
    return (
      <Stack gap={4}>
        <Title order={5}>{title}</Title>
        <Text size="sm" c="dimmed">
          {empty}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      <Title order={5}>{title}</Title>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{title === "Landing pages" ? "Page" : "Name"}</Table.Th>
            <Table.Th ta="right">Sessions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.label}>
              <Table.Td>{renderLabel ? renderLabel(row) : <Text size="sm">{row.label}</Text>}</Table.Td>
              <Table.Td ta="right">{row.sessions.toLocaleString()}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function displayPath(url) {
  if (!url) return "—";
  try {
    const parsed = new URL(url);
    return (parsed.pathname || "/") + parsed.search;
  } catch {
    return url;
  }
}
